import { createJSONStorage, devtools, persist } from "zustand/middleware";
import { immer } from "zustand/middleware/immer";

import { create } from "@/lib/store";
import { DETAILED_COLUMNS } from "@/pages/channel-roi/constants/defaults";
import {
  MROIChartType,
  MROIColumnSelector,
  MROIGridFilters,
} from "@/pages/channel-roi/constants/types";
import { BusinessUnitToggle, GeoViewToggle, MetricType } from "@/shared/types";
import { hashStorage } from "@/shared/utils/hashStorage";

const STORE_NAME = "MROI";

const DEFAULT_GRID_FILTERS = [
  MROIGridFilters.TOTAL_SPEND_AMOUNT,
  MROIGridFilters.TOTAL_REVENUE_AMOUNT,
  MROIGridFilters.TOTAL_ROI_PERCENTAGE,
  MROIGridFilters.TOTAL_SPEND_PERCENTAGE,
  MROIGridFilters.TOTAL_REVENUE_PERCENTAGE,
  MROIGridFilters.CONTRIBUTION_PERCENTAGE,
];

interface MROIState {
  compareDateRange: {
    startDate: string | null;
    endDate: string | null;
  };
  filters: {
    targetNodeUUID: string | null;
    compareNodeUUIDs: string[];
    funnelStage: string | null;
    channels: string[];
    geographies: string[];
  };
  chart: {
    channelEffectiveness: {
      chartType: MROIChartType;
      column: {
        selector: MROIColumnSelector;
      };
    };

    isAscending: boolean;
    showInsufficientData: boolean;
  };
  grid: {
    filters: MROIGridFilters[];
    geoView: GeoViewToggle;
    businessUnitToggle: BusinessUnitToggle;
    metricType: MetricType;
    activeColumns: string[];
  };
}

interface MROIActions {
  setTargetNodeUUID: (nodeUUID: string | null) => void;
  setCompareNodeUUIDs: (compareNodeUUIDs: string[]) => void;
  setFunnelStage: (funnelStage: string | null) => void;
  setChannels: (channels: string[]) => void;
  setGeographies: (geographies: string[]) => void;
  setChartType: (chartType: MROIChartType) => void;
  setColumnSelector: (selector: MROIColumnSelector) => void;
  setGridFilters: (filters: MROIGridFilters[]) => void;
  setCompareDateRange: (startDate: string | null, endDate: string | null) => void;
  setGeoView: (geoView: GeoViewToggle) => void;
  setBusinessUnitToggle: (businessUnitToggle: BusinessUnitToggle) => void;
  setMetricType: (metricType: MetricType) => void;
  setIsAscending: (isAscending: boolean) => void;
  setShowInsufficientData: (showInsufficientData: boolean) => void;
  setActiveColumns: (columns: string[]) => void;
}

type MROIStore = MROIState & MROIActions;

const defaultState: MROIState = {
  compareDateRange: {
    startDate: null,
    endDate: null,
  },
  filters: {
    targetNodeUUID: null,
    compareNodeUUIDs: [],
    funnelStage: null,
    channels: [],
    geographies: [],
  },
  chart: {
    channelEffectiveness: {
      chartType: MROIChartType.BUBBLE,
      column: {
        selector: MROIColumnSelector.SPEND_VS_REVENUE_AMOUNT,
      },
    },
    isAscending: false,
    showInsufficientData: true,
  },
  grid: {
    filters: DEFAULT_GRID_FILTERS,
    geoView: GeoViewToggle.AGGREGATE,
    businessUnitToggle: BusinessUnitToggle.AGGREGATE,
    metricType: MetricType.MARKETING_ROI,
    activeColumns: DETAILED_COLUMNS,
  },
};

export const useMROIStore = create<MROIStore>()(
  devtools(
    persist(
      immer((set) => ({
        ...defaultState,
        setTargetNodeUUID: (nodeUUID) =>
          set((state) => {
            state.filters.targetNodeUUID = nodeUUID;
            state.filters.compareNodeUUIDs = [];
          }),
        setCompareNodeUUIDs: (compareNodeUUIDs: string[]) =>
          set((state) => {
            state.filters.compareNodeUUIDs = compareNodeUUIDs;
          }),
        setFunnelStage: (funnelStage) =>
          set((state) => {
            state.filters.funnelStage = funnelStage;
          }),
        setChannels: (channels) =>
          set((state) => {
            state.filters.channels = channels;
          }),
        setGeographies: (geographies) =>
          set((state) => {
            state.filters.geographies = geographies;
          }),
        setChartType: (chartType) =>
          set((state) => {
            state.chart.channelEffectiveness.chartType = chartType;
          }),
        setColumnSelector: (selector) =>
          set((state) => {
            state.chart.channelEffectiveness.column.selector = selector;
          }),
        setGridFilters: (filters) =>
          set((state) => {
            state.grid.filters = filters;
          }),
        setCompareDateRange: (startDate, endDate) =>
          set((state) => {
            state.compareDateRange.startDate = startDate;
            state.compareDateRange.endDate = endDate;
          }),
        setGeoView: (geoView) =>
          set((state) => {
            state.grid.geoView = geoView;
          }),
        setBusinessUnitToggle: (businessUnitToggle) =>
          set((state) => {
            state.grid.businessUnitToggle = businessUnitToggle;
          }),
        setMetricType: (metricType) =>
          set((state) => {
            state.grid.metricType = metricType;
          }),
        setIsAscending: (isAscending) =>
          set((state) => {
            state.chart.isAscending = isAscending;
          }),
        setShowInsufficientData: (showInsufficientData) =>
          set((state) => {
            state.chart.showInsufficientData = showInsufficientData;
          }),
        setActiveColumns: (columns) =>
          set((state) => {
            state.grid.activeColumns = columns;
          }),
      })),
      {
        name: STORE_NAME,
        storage: createJSONStorage(() => hashStorage),
      }
    ),
    { name: STORE_NAME }
  )
);
